import { useEffect, useState } from 'react';
import { useEditor } from './state/store';
import { useLuna } from './state/luna-store';
import { Sidebar } from './components/Sidebar';
import { Preview } from './components/Preview';
import { Inspector } from './components/Inspector';
import { Toolbar } from './components/Toolbar';
import { CodeMode } from './components/code/CodeMode';
import { LunaPanel } from './components/luna/LunaPanel';
import { UnsavedDraftBanner } from './components/UnsavedDraftBanner';
import { ThemeSyncBanner } from './components/ThemeSyncBanner';
import { SaveToast } from './components/Toast';
import type { EditorSession } from './api/client';

function useSession(): [EditorSession | null, (s: EditorSession | null) => void] {
  const [session, setSession] = useState<EditorSession | null>(() => {
    const u = new URL(window.location.href);
    const tenantId = u.searchParams.get('tenantId');
    const themeId = u.searchParams.get('themeId');
    const token = u.searchParams.get('token');
    const host = u.searchParams.get('host') || undefined;
    if (tenantId && themeId && token) {
      const s: EditorSession = { tenantId, themeId, token, storeHost: host || undefined };
      localStorage.setItem('lc-editor-session', JSON.stringify(s));
      return s;
    }
    const raw = localStorage.getItem('lc-editor-session');
    if (raw) {
      try { return JSON.parse(raw); } catch {}
    }
    return null;
  });
  return [
    session,
    (s) => {
      if (s) localStorage.setItem('lc-editor-session', JSON.stringify(s));
      else localStorage.removeItem('lc-editor-session');
      setSession(s);
    },
  ];
}

function BootScreen({ onReady }: { onReady(s: EditorSession): void }) {
  const [tenantId, setTenantId] = useState('');
  const [themeId, setThemeId] = useState('default');
  const [token, setToken] = useState('');
  const [host, setHost] = useState('');

  return (
    <div className="flex h-full items-center justify-center bg-surface-subdued">
      <form
        className="w-[400px] rounded-xl border border-border bg-surface p-6 shadow-card"
        onSubmit={(e) => {
          e.preventDefault();
          if (!tenantId || !themeId || !token) return;
          onReady({ tenantId, themeId, token, storeHost: host || undefined });
        }}
      >
        <div className="mb-4 flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-text-inverse">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M12 3 4 7v10l8 4 8-4V7l-8-4Z" />
              <path d="m4 7 8 4 8-4" />
              <path d="M12 11v10" />
            </svg>
          </div>
          <div>
            <h1 className="text-[15px] font-semibold text-text">Editor de tema</h1>
            <p className="text-[12px] text-text-subdued">
              Conecte-se a um tenant para começar.
            </p>
          </div>
        </div>

        <Field label="Tenant ID" value={tenantId} onChange={setTenantId} placeholder="ex: imperio-multibrabas" />
        <Field label="Theme ID" value={themeId} onChange={setThemeId} placeholder="default" />
        <Field label="Token" value={token} onChange={setToken} placeholder="Bearer…" />
        <Field label="Host da loja (opcional)" value={host} onChange={setHost} placeholder="loja.com.br" />
        <button
          type="submit"
          className="lc-btn-primary mt-2 w-full"
          disabled={!tenantId || !themeId || !token}
        >
          Abrir editor
        </button>
      </form>
    </div>
  );
}

function Field({
  label, value, onChange, placeholder,
}: { label: string; value: string; onChange(v: string): void; placeholder?: string }) {
  return (
    <label className="mb-3 block">
      <span className="mb-1 block text-[12px] font-medium text-text">{label}</span>
      <input
        className="lc-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </label>
  );
}

export function App() {
  const [session, setSession] = useSession();
  const boot = useEditor((s) => s.boot);
  const error = useEditor((s) => s.error);
  const metadata = useEditor((s) => s.metadata);
  const mode = useEditor((s) => s.mode);
  const setMode = useEditor((s) => s.setMode);
  const inspectorOpen = useEditor((s) => s.inspectorOpen);

  // Luna Intelligence — shared across visual + code modes. Mounts once
  // and stays available from anywhere via the purple/pink button.
  const lunaOpen = useLuna((s) => s.open);
  const setLunaOpen = useLuna((s) => s.setOpen);
  const initLuna = useLuna((s) => s.init);
  const lunaSession = useLuna((s) => s.session);

  // Pick initial mode from URL (?mode=code) and keep it in sync on history nav
  useEffect(() => {
    const sync = () => {
      const u = new URL(window.location.href);
      const m = u.searchParams.get('mode');
      if (m === 'code' || m === 'visual') {
        if (m !== useEditor.getState().mode) setMode(m);
      }
    };
    sync();
    window.addEventListener('popstate', sync);
    return () => window.removeEventListener('popstate', sync);
  }, [setMode]);

  useEffect(() => {
    if (session) void boot(session, 'index');
  }, [session, boot]);

  useEffect(() => {
    if (session && (!lunaSession || lunaSession.tenantId !== session.tenantId)) {
      void initLuna(session);
    }
  }, [session, lunaSession, initLuna]);

  if (!session) return <BootScreen onReady={(s) => setSession(s)} />;

  // CodeMode mounts its own Luna panel + button (three-column layout
  // puts it in the gutter), so we only render the floating Luna UI in
  // visual mode to avoid doubling up.
  const showFloatingLuna = mode === 'visual';

  return (
    <div className="flex h-full flex-col bg-surface-subdued">
      <Toolbar />
      <ThemeSyncBanner />
      <UnsavedDraftBanner />
      <SaveToast />
      {error && !metadata ? (
        <div className="flex flex-1 items-center justify-center bg-surface-subdued">
          <div className="max-w-md rounded-xl border border-critical/30 bg-critical/5 p-5 text-center">
            <h2 className="text-[14px] font-semibold text-critical">
              Não foi possível carregar o editor
            </h2>
            <p className="mt-1 text-[12px] text-text-subdued">{error}</p>
            <button
              className="lc-btn-secondary mt-4"
              onClick={() => setSession(null)}
            >
              Voltar
            </button>
          </div>
        </div>
      ) : mode === 'code' ? (
        <CodeMode />
      ) : (
        <div className="flex min-h-0 flex-1">
          <Sidebar />
          <Preview />
          {inspectorOpen && <Inspector />}
          {lunaOpen && <LunaPanel />}
        </div>
      )}

      {showFloatingLuna && (
        <button
          onClick={() => setLunaOpen(!lunaOpen)}
          className="fixed bottom-5 right-5 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 text-white shadow-[0_8px_24px_rgba(124,58,237,0.35)] transition-transform hover:scale-105"
          title={lunaOpen ? 'Esconder Luna Intelligence' : 'Abrir Luna Intelligence'}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="4" />
            <path d="M12 2v2M12 20v2M4 12H2M22 12h-2M6 6 4.5 4.5M19.5 19.5 18 18M6 18l-1.5 1.5M19.5 4.5 18 6" />
          </svg>
        </button>
      )}
    </div>
  );
}
